import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { isEqual } from "lodash";
import { FIELD_VISIBILITY, ACR_STATUS, SUBMISSION_STATUS } from "../constants/acr";
import { isEmptyValue } from "./formValueUtils";
export var truncateMiddle = function truncateMiddle(text) {
  var maxLength = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 30;
  if (text.length <= maxLength) return text;
  var charsToShow = maxLength - 6; // Reserve 6 characters for "......"
  var frontChars = Math.ceil(charsToShow / 2);
  var backChars = Math.floor(charsToShow / 2);
  return text.substring(0, frontChars) + "......" + text.substring(text.length - backChars);
};
export var transformFieldConfigs = function transformFieldConfigs(configs) {
  if (!configs || configs.length === 0) return [];
  return configs.map(function (field) {
    return _objectSpread(_objectSpread({}, field), {}, {
      visibility: field.visibility,
      // Fields with visibility required or optional are enabled
      enabled: field.visibility === FIELD_VISIBILITY.REQUIRED || field.visibility === FIELD_VISIBILITY.OPTIONAL,
      value: field.static_value || null
    });
  });
};
export var buildFieldUpdates = function buildFieldUpdates(currentConfigs, initialConfigs) {
  var updates = {};

  // Helper to normalize empty values for comparison
  var normalizeValue = function normalizeValue(value) {
    return value === null || value === undefined || value === "" ? "" : value;
  };
  currentConfigs.forEach(function (field, index) {
    var _field$metadata, _initialField$metadat;
    var initialField = initialConfigs[index];
    if (!initialField) return;
    var visibilityChanged = field.visibility !== initialField.visibility;

    // Use deep equality for value comparison to handle arrays/objects correctly
    var normalizedCurrentValue = normalizeValue(field.value);
    var normalizedInitialValue = normalizeValue(initialField.value);
    var valueChanged = !isEqual(normalizedCurrentValue, normalizedInitialValue);
    var validValuesChanged = !isEqual((_field$metadata = field.metadata) === null || _field$metadata === void 0 ? void 0 : _field$metadata.valid_values, (_initialField$metadat = initialField.metadata) === null || _initialField$metadat === void 0 ? void 0 : _initialField$metadat.valid_values);
    if (visibilityChanged || valueChanged || validValuesChanged) {
      var update = {};
      if (visibilityChanged) {
        update.visibility = field.visibility;
      }
      if (valueChanged) {
        update.static_value = field.value || "";
      }
      if (validValuesChanged) {
        var _field$metadata2;
        update.valid_values = ((_field$metadata2 = field.metadata) === null || _field$metadata2 === void 0 ? void 0 : _field$metadata2.valid_values) || [];
      }
      updates[field.field_name] = update;
    }
  });
  return updates;
};
export var transformWorkspaceData = function transformWorkspaceData(workspaceData, defaultWorkspaceId) {
  if (!(workspaceData !== null && workspaceData !== void 0 && workspaceData.owned_workspaces)) {
    return {
      sources: [],
      defaultSource: null
    };
  }

  // Map owned workspaces (owner is always admin)
  var ownedSources = workspaceData.owned_workspaces.map(function (src) {
    return {
      label: src.name,
      value: src.id,
      id: src.uuid,
      role: "admin",
      has_permission: true,
      synced: false
    };
  });

  // Map shared workspaces - ONLY include workspaces where user has admin role
  // Security: This prevents privilege escalation where a user who is admin of workspace A
  // could access ACR settings for workspace B where they're only a regular member
  var sharedSources = (workspaceData.shared_workspaces || []).filter(function (src) {
    return src.role === "admin";
  }).map(function (src) {
    return {
      label: src.name,
      value: src.id,
      id: src.uuid,
      role: src.role,
      has_permission: true,
      synced: false
    };
  });

  // Combine owned and shared workspaces (all have admin privileges)
  var sources = [].concat(_toConsumableArray(ownedSources), _toConsumableArray(sharedSources));

  // Default to the user's default workspace only if they have admin access to it,
  // otherwise fall back to the first available admin workspace
  var defaultSource = sources.find(function (item) {
    return item.value === defaultWorkspaceId;
  }) || sources[0] || null;
  return {
    sources: sources,
    defaultSource: defaultSource
  };
};
export var validateRegistrationForm = function validateRegistrationForm(form, fields) {
  var emptyFields = fields.filter(function (field) {
    return !form[field.id] || form[field.id].trim() === "";
  });
  return {
    isValid: emptyFields.length === 0,
    emptyFields: emptyFields
  };
};
export var initializeRegistrationForm = function initializeRegistrationForm(fields) {
  var initialState = {};
  fields.forEach(function (field) {
    initialState[field.id] = "";
  });
  return initialState;
};
export var syncFormWithConfig = function syncFormWithConfig(currentForm, workspaceConfig) {
  return _objectSpread(_objectSpread({}, currentForm), {}, {
    facility_id: workspaceConfig.facility_id || "",
    partner_id: workspaceConfig.partner_id || "",
    app_id: workspaceConfig.app_id || "",
    facility_name: workspaceConfig.facility_name || "",
    client_id: workspaceConfig.client_id || "",
    redirect_uri: workspaceConfig.redirect_uri || ""
  });
};

// Converts ACRFieldConfig to SubmissionFieldSchema format
// Both share the same shape, so this is essentially a passthrough
export var convertFieldConfigToSchema = function convertFieldConfigToSchema(fieldConfig) {
  var metadata = fieldConfig.metadata || {};

  // Build validation rules from metadata
  var validationRules = metadata.pattern || metadata.format_hint || metadata.min_value !== null || metadata.max_value !== null ? {
    pattern: metadata.pattern || undefined,
    format_hint: metadata.format_hint || undefined,
    min_value: metadata.min_value || undefined,
    max_value: metadata.max_value || undefined
  } : null;
  return {
    field_name: fieldConfig.field_name,
    label: fieldConfig.label,
    section: fieldConfig.section,
    sub_section: fieldConfig.sub_section,
    display_order: fieldConfig.display_order,
    visibility: fieldConfig.visibility,
    source: fieldConfig.source,
    static_value: fieldConfig.static_value,
    metadata: metadata,
    is_acr_required: metadata.is_acr_required,
    validation_rules: validationRules
  };
};
export var convertFieldConfigsToSchemas = function convertFieldConfigsToSchemas(fieldConfigs) {
  return fieldConfigs.map(convertFieldConfigToSchema);
};

/**
 * Comprehensive check for whether a field is required.
 * Consolidates all required field checks in one place:
 * - Static required (field.is_acr_required)
 * - Conditional required (conditionalError with type "required")
 * - Special check required (special_check error type)
 * - Server missing (field in missing_fields array)
 *
 * Note: Does NOT mark as required for "should_be_blank" conditional errors
 */
export var checkFieldRequiredStatus = function checkFieldRequiredStatus(field, fieldErrorsList) {
  var _fieldError$condition;
  var isServerMissing = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  // 1. Check static required
  var isStaticRequired = field.is_acr_required || field.visibility === "required";

  // 2. Check conditional required (ONLY type "required", NOT "should_be_blank")
  var fieldError = fieldErrorsList === null || fieldErrorsList === void 0 ? void 0 : fieldErrorsList.find(function (err) {
    return err.field === field.field_name;
  });
  var isConditionalRequired = (fieldError === null || fieldError === void 0 ? void 0 : fieldError.conditional_metadata) !== undefined && (fieldError === null || fieldError === void 0 || (_fieldError$condition = fieldError.conditional_metadata) === null || _fieldError$condition === void 0 ? void 0 : _fieldError$condition.type) === "required";

  // 3. Check special check required (ONLY "required", NOT "optional")
  var hasSpecialCheckError = fieldErrorsList === null || fieldErrorsList === void 0 ? void 0 : fieldErrorsList.some(function (error) {
    var _error$conditional_me;
    return error.field === field.field_name && error.type === "special_check" && ((_error$conditional_me = error.conditional_metadata) === null || _error$conditional_me === void 0 ? void 0 : _error$conditional_me.type) === "required";
  });

  // 4. Check server missing
  return isStaticRequired || isConditionalRequired || hasSpecialCheckError || isServerMissing;
};

/**
 * Calculate required fields status (filled vs total)
 * Used by both SubmissionFormContent and GroupedFormSection to ensure consistent logic
 */
export var calculateRequiredFieldsStatus = function calculateRequiredFieldsStatus(fields, formData, conditionalErrors, missingFields) {
  var requiredFields = fields.filter(function (field) {
    var _conditionalError$con, _conditionalError$con2;
    // Check if statically required using is_acr_required
    var isStaticRequired = field.is_acr_required || field.visibility === "required";

    // Check if conditionally required or required by special checks
    var conditionalError = conditionalErrors === null || conditionalErrors === void 0 ? void 0 : conditionalErrors.find(function (err) {
      return err.field === field.field_name;
    });
    var isConditionalRequired = (conditionalError === null || conditionalError === void 0 ? void 0 : conditionalError.type) === "conditional" && (conditionalError === null || conditionalError === void 0 || (_conditionalError$con = conditionalError.conditional_metadata) === null || _conditionalError$con === void 0 ? void 0 : _conditionalError$con.type) === "required" || (conditionalError === null || conditionalError === void 0 ? void 0 : conditionalError.type) === "special_check" && (conditionalError === null || conditionalError === void 0 || (_conditionalError$con2 = conditionalError.conditional_metadata) === null || _conditionalError$con2 === void 0 ? void 0 : _conditionalError$con2.type) === "required";

    // Check if field is in server missing_fields array
    var isServerMissing = (missingFields === null || missingFields === void 0 ? void 0 : missingFields.includes(field.field_name)) || false;
    return isStaticRequired || isConditionalRequired || isServerMissing;
  });
  var filledFields = requiredFields.filter(function (field) {
    var value = formData[field.field_name];
    // Consider field filled if it has a non-empty value
    return !isEmptyValue(value);
  });
  var emptyFields = requiredFields.filter(function (field) {
    var value = formData[field.field_name];
    // Consider field empty
    return isEmptyValue(value);
  });
  return {
    totalRequired: requiredFields.length,
    filled: filledFields.length,
    empty: emptyFields.length,
    emptyFields: emptyFields
  };
};
export var getReportDisplayTitle = function getReportDisplayTitle(report) {
  if (!report) {
    return "ACR Submission";
  }
  // Use report_heading if available, otherwise fall back to report ID
  return report.report_heading || "Report #".concat(report.report_id || report.id);
};
export var isPollableStatus = function isPollableStatus(acrStatus, status) {
  var internalStatus = status.toLowerCase();
  return acrStatus === ACR_STATUS.ENQUEUED || acrStatus === ACR_STATUS.LOADING || acrStatus === ACR_STATUS.RECEIVED || acrStatus === ACR_STATUS.POSTED || acrStatus === ACR_STATUS.PARTIALLY_SUCCESSFUL || acrStatus === ACR_STATUS.VALIDATION_FAIL || internalStatus === SUBMISSION_STATUS.SUBMITTED || internalStatus === SUBMISSION_STATUS.PARTIALLY_SUCCESSFUL || internalStatus === SUBMISSION_STATUS.VALIDATION_FAILED;
};